import { deadlinePressure, formatCountdown, formatRemaining, type DeadlinePressure } from '@ovrsee/shared'
import { Chip } from '../ui/index.js'
import { now, useCountdown } from '../lib/time.js'

const TONE: Record<DeadlinePressure, 'positive' | 'warning' | 'negative'> = {
  calm: 'positive',
  warning: 'warning',
  urgent: 'negative',
}

/**
 * Ticks once a SECOND, from one shared timer (see useCountdown). An earlier version ticked
 * once a minute because the display was days-and-hours and per-second bought nothing
 * visible; the owner asked for a visibly counting clock on the employee cards, so the
 * seconds are now the point. Cost is controlled by sharing one interval, not by ticking
 * slowly.
 *
 * `live` also changes what expiry looks like: a live clock reads 00:00:00 and pulses,
 * which is what the owner asked for. The calmer non-live form keeps its caller-supplied
 * expiredLabel. The tone is never the only signal — the card renders a separate status
 * chip reading "Overdue" beside this, so the state survives greyscale and colour-blindness.
 */

export function Countdown({
  deadlineIso,
  assignedAtIso,
  expiredLabel,
  live = false,
}: {
  deadlineIso: string
  assignedAtIso?: string
  expiredLabel?: string
  live?: boolean
}) {
  const value = useCountdown(deadlineIso)
  const text = live
    ? formatCountdown(value)
    : value.expired && expiredLabel !== undefined
      ? expiredLabel
      : formatRemaining(value)
  if (assignedAtIso === undefined) {
    return (
      <span className="mono tnum" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
        {text}
      </span>
    )
  }

  const pressure = deadlinePressure(new Date(assignedAtIso), new Date(deadlineIso), now())
  // Semibold, not a louder colour. The palette is deliberately muted and the filled
  // tones sit near 3:1 against their own backgrounds, so weight is what carries the
  // "notice me" the countdown needs without introducing a colour the design system
  // does not have.
  return (
    <Chip
      label={text}
      color={TONE[pressure]}
      filled
      className={value.expired ? 'countdown-expired' : undefined}
      style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}
    />
  )
}
